import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getJobProfileDrafts } from '@/api/jobProfile';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppPagination } from '@/stores/app/pagination';
import { useFilters } from '@/stores/filters/filters';
import { isEqual } from 'lodash-es';
import { removeEmptyParams } from '@/stores/filters/helpers';
import { useAppRoute } from '@/stores/app/route';

export const useJobProfileDrafts = defineStore('jobProfileDrafts', () => {
  const { setLoading } = useAppLoading();
  const { updatePagination } = useAppPagination();
  const { updateFilters } = useFilters();
  const routeStore = useAppRoute();

  const { myJobsEnabled, searchTerm } = storeToRefs(useFilters());

  const jobProfileDrafts = ref([]);
  const totals = ref([]);

  const loadJobProfileDrafts = async ({
    mode,
    routeQuery,
    router,
    selectedFilters,
    page,
    searchTerm: newSearchTerm,
    selectedMyJobs,
    selectedSubMenuOption: subMenuOption,
  }) => {
    setLoading({ pageLoading: true, listLoading: true });
    try {
      const params = routeQuery ? { ...routeQuery } : {};

      if (!params.sort && !subMenuOption) {
        params.sort = 'title';
      } else if (subMenuOption) {
        params.sort = subMenuOption;
      }

      params.page = page || undefined;

      const parameters = {
        mode,
        params,
        selectedMyJobs,
        searchTerm: newSearchTerm,
        selectedFilters,
      };

      const newJobProfileDrafts = await getJobProfileDrafts(parameters);

      jobProfileDrafts.value = newJobProfileDrafts.documents;
      totals.value = newJobProfileDrafts.totals;

      updatePagination(newJobProfileDrafts.pagination);

      updateFilters({
        filters: newJobProfileDrafts.filters,
        subMenuOptions: newJobProfileDrafts.sorting,
        myJobsEnabled: newJobProfileDrafts.owned,
        searchTerm: newJobProfileDrafts.textFilter,
      });

      if (
        router
        && !isEqual(
          removeEmptyParams(routeStore.route?.query),
          removeEmptyParams(routeQuery),
        )
      ) {
        const newQuery = { ...selectedFilters, ...params };

        if (myJobsEnabled.value) {
          newQuery.owned = myJobsEnabled.value;
        }

        if (searchTerm.value) {
          newQuery.q = searchTerm.value;
        }

        router.push({
          query: newQuery,
        }).catch(() => true);
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile drafts load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ pageLoading: false, listLoading: false });
    }
  };

  return {
    jobProfileDrafts,
    loadJobProfileDrafts,
  };
});
